import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

function isChunkLoadError(error) {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Loading CSS chunk') ||
    error?.message?.includes('Failed to fetch dynamically imported module')
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Auto-reload on chunk load errors (stale deployment)
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem('chunk_error_reload');
      const now = Date.now();
      // Only auto-reload once per 30 seconds to prevent reload loops
      if (!lastReload || now - Number(lastReload) > 30000) {
        sessionStorage.setItem('chunk_error_reload', String(now));
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const chunkError = isChunkLoadError(this.state.error);

      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', p: 3 }}>
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {chunkError ? 'Update Available' : 'Something went wrong'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {chunkError
                ? 'A new version of the app has been deployed. Please reload to get the latest version.'
                : (this.state.error?.message || 'An unexpected error occurred in this section.')}
            </Typography>
            {chunkError ? (
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleReload}>
                Reload Page
              </Button>
            ) : (
              <Button variant="contained" onClick={this.handleReset}>
                Try Again
              </Button>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
